import { Directive, HostListener, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { UtilsService } from 'src/app/services/utils.service';

@Directive({
  selector: '[appPhoneMask]',
})
export class PhoneFormaterDirective {
  constructor(private utilService: UtilsService, @Optional() @Self() private ngControl: NgControl) {}

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const input = event.target as HTMLInputElement;

    // Remember how many digits sat before the caret so we can restore an equivalent
    // position after reformatting. Reassigning `input.value` otherwise forces the caret
    // to the end, which makes editing the middle of an existing number (e.g. the area
    // code) jump to the end on every keystroke.
    const caretStart = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = this.countDigits(input.value.slice(0, caretStart));

    // Keep only digits and cap at 10 (US number). Stripping non-digits first avoids
    // counting the mask hyphens toward the length limit.
    // NOTE (known follow-up): a leading country-code digit in an 11-digit paste
    // (e.g. "12025551234") is not stripped; it is kept as part of the area code.
    const digits = input.value.replace(/\D/g, '').substring(0, 10);
    const formatted = this.utilService.getPhoneNumberFormated(digits);

    input.value = formatted;
    this.restoreCaret(input, digitsBeforeCaret);

    // The DefaultValueAccessor updates the FormControl from the native `input` event,
    // whose firing order relative to this directive is not guaranteed. Push the masked
    // value into the control explicitly so the model never diverges from what is shown.
    //
    // `emitEvent: false` is deliberate: the DefaultValueAccessor already emitted for this
    // keystroke, so this call only needs to correct the stored value + re-run validators
    // (updateValueAndValidity still runs synchronously). The trade-off is that any future
    // `valueChanges` subscriber on the phone control will NOT see this corrected value — no
    // current consumer subscribes, but a live-preview/analytics hook would need emitEvent:true.
    if (this.ngControl?.control && this.ngControl.control.value !== formatted) {
      this.ngControl.control.setValue(formatted, { emitEvent: false });
    }
  }

  private countDigits(value: string): number {
    return value.replace(/\D/g, '').length;
  }

  private restoreCaret(input: HTMLInputElement, digitsBeforeCaret: number): void {
    if (typeof input.setSelectionRange !== 'function') {
      return;
    }
    // Walk the formatted value until the same number of digits precede the caret.
    let seenDigits = 0;
    let position = input.value.length;
    if (digitsBeforeCaret <= 0) {
      position = 0;
    } else {
      for (let i = 0; i < input.value.length; i++) {
        if (/\d/.test(input.value[i])) {
          seenDigits++;
          if (seenDigits === digitsBeforeCaret) {
            position = i + 1;
            break;
          }
        }
      }
    }
    try {
      input.setSelectionRange(position, position);
    } catch {
      // Some input types (e.g. number/email) don't support selection APIs; ignore.
    }
  }
}
